<script setup lang="ts">
import { useUserFetch } from '~/composables/useUserApi';
import type { Employee } from '~/models/employee';
import type { OpeningHours, Day, Hours } from '~/models/openinghours';
import LInput from '~/components/linput.vue';
import { useDayjs } from '#dayjs'
type IsCollapsed = {
  [key: string]: boolean;
}

const { data: openingHours, refresh, pending } = await useUserFetch<OpeningHours[]>('/api/openingHours');
const dayjs = useDayjs();
dayjs.locale('nb');
const now = dayjs();

const isCollapsed = useState<IsCollapsed>('isCollapsed', () => {
  if (!openingHours.value) {
    return {};
  }
  return openingHours.value.reduce((acc, o) => ({
    ...acc,
    [o.where]: false
  }), {});
});


function isOpenNow(openinghour: OpeningHours) {
  try {
    const daystring = now.format('dddd');
    const day = daystring[0].toUpperCase() + daystring.slice(1) as Day;
    const hours = openinghour.days[day];
    if (!hours || !hours.open) {
      return false;
    }

    if (hours.lunch) {
      const lunchFromNumber = hours.lunchFrom.split(':').map(Number);
      const lunchToNumber = hours.lunchTo.split(':').map(Number);
      const lunchFrom = dayjs().hour(lunchFromNumber[0]).minute(lunchFromNumber[1]).second(0);
      const lunchTo = dayjs().hour(lunchToNumber[0]).minute(lunchToNumber[1]).second(0);
      const isInLunch = now.isAfter(lunchFrom) && now.isBefore(lunchTo);
      if (isInLunch) {
        return false;
      }
    }

    const fromNumber = hours.from.split(':').map(Number);
    const toNumber = hours.to.split(':').map(Number);
    const from = dayjs().hour(fromNumber[0]).minute(fromNumber[1]).second(0);
    const to = dayjs().hour(toNumber[0]).minute(toNumber[1]).second(0);
    const isOpen = now.isAfter(from) && now.isBefore(to);

    return isOpen;
  } catch (error) {
    console.error(error);
    return false;
  }
}

function getOpeningHours(hour: Hours) {
  if (!hour.open) {
    return 'Stengt';
  }
  if (hour.lunch) {
    return `${hour.from} - ${hour.lunchFrom} | ${hour.lunchTo} - ${hour.to}`;
  }
  return `${hour.from} - ${hour.to}`;
}

function isDayToday(day: Day) {

  switch (now.day()) {
    case 0:
      return day === 'Søndag';
    case 1:
      return day === 'Mandag';
    case 2:
      return day === 'Tirsdag';
    case 3:
      return day === 'Onsdag';
    case 4:
      return day === 'Torsdag';
    case 5:
      return day === 'Fredag';
    case 6:
      return day === 'Lørdag';
  }
  return false;
}

</script>
<template>
  <div class="p-4 mainColor container mx-auto max-w-screen-lg">

    <SectionHeader header="Åpningstider">
      <p>
        Adresse, telefonnummer og åpningstider - det finner du her:</p>
      <p class="text-xl hover:underline my-4">Telefon: <a href="tel:69283150">69 28 31 50</a></p>
      <p class="text-xl hover:underline my-4">Adresse: <a target="_blank"
          href="https://www.google.com/maps/place/Sarpsborgveien%207%20E,+1640+R%C3%A5de">Sarpsborgveien 7 E, 1640
          Råde</a></p>
    </SectionHeader>



    <div v-if="!pending" class="flex flex-col gap-6">
      <div v-for="openinghour in openingHours" :key="openinghour.id"
        class="rounded-lg shadow-md border-1 border-[#dfdfdf]">
        <div class="flex flex-row gap-2 justify-between hover:shadow-sm hover:bg-slate-250 p-4"
          @click="isCollapsed[openinghour.where] = !isCollapsed[openinghour.where]">
          <!-- <Icon name="hugeicons:labs" class="h-full" /> -->
          <div class="flex flex-col">
            <h2 class="text-xl font-bold mb-2 mainColorText">{{ openinghour.where }}</h2>
            <div v-if="isOpenNow(openinghour)" class="flex flex-row gap-2 text-lg font-bold mb-1" style="color:#00a839">
              <span>Åpent</span>
              <Icon name='bxs:door-open' size="1.5em" />
            </div>
            <div class="text-lg font-bold mb-1 flex flex-row gap-2" style="color:#a83900" v-else>
              <span class='align-middle'>Stengt</span>
              <Icon name='bxs:lock' size="1.5em" />
            </div>
          </div>
          <Icon name="hugeicons:arrow-up-01" size="2em" v-if="isCollapsed[openinghour.where] === true" />
          <Icon name="hugeicons:arrow-down-01" size="2em" v-else />
        </div>
        <div class="p-4" v-if="isCollapsed[openinghour.where] === true">
          <div v-for="hr, day, idx in openinghour.days" :key="day"
            class="flex flex-row gap-2 justify-center border-[#dfdfdf] p-2 mainColorText"
            :class="{ 'border-b-1': idx !== Object.keys(openinghour.days).length - 1, 'font-bold': isDayToday(day) }">
            <div>{{ day }}: </div>
            <div>
              {{ getOpeningHours(hr) }}
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
